import React, { useState, useRef } from 'react';
import { Send, ArrowRight, Upload } from 'lucide-react';
import PhoneInputNative from '../PhoneInputNative';

const CoachingForm = () => {
    const [submitted, setSubmitted] = useState(false);
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+44');
    const [selectedCountry, setSelectedCountry] = useState('GB');
    const [region, setRegion] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleCountryCodeChange = (code: string, country: string) => {
        setCountryCode(code);
        setSelectedCountry(country);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log("Submitting Coaching Form...");

        const formData = new FormData(e.target as HTMLFormElement);
        formData.set('phone', countryCode + phone);
        formData.set('country', selectedCountry);
        formData.set('state', region);
        formData.set('service', 'coaching');

        try {
            await fetch('/api/eligibility', {
                method: 'POST',
                body: formData
            });
        } catch (err) {
            console.error("Submission error:", err);
        }

        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
    };

    if (submitted) {
        return (
            <div className="text-center py-12 animate-fadeIn">
                <div className="w-16 h-16 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send size={32} />
                </div>
                <h3 className="text-2xl font-serif text-[#011E26] mb-2">Thank you for submitting your details.</h3>
                <p className="text-slate-600 max-w-lg mx-auto">
                    A member of the Nex Advisory team will review your information and get in touch with you.
                </p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            <div className="mb-8">
                <h3 className="text-2xl font-serif text-[#011E26] mb-2">Pre-Arrival Coaching – Eligibility Form</h3>
                <p className="text-slate-600">At Nex, we want to help you feel fully prepared before you arrive in the UK.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* 1. Personal Information */}
                <div>
                    <h4 className="text-lg font-bold text-[#009B8F] mb-4 border-b border-gray-100 pb-2">1. Personal Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Full Name</label>
                            <input required type="text" name="name" className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Email Address</label>
                            <input required type="email" name="email" className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Phone Number</label>
                            <PhoneInputNative
                                phone={phone}
                                countryCode={countryCode}
                                onPhoneChange={setPhone}
                                onCountryCodeChange={handleCountryCodeChange}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">State / Province</label>
                            <input
                                type="text"
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none"
                                placeholder="Enter your state or province"
                            />
                        </div>
                    </div>
                </div>

                {/* 2. Visa & Arrival Status */}
                <div>
                    <h4 className="text-lg font-bold text-[#009B8F] mb-4 border-b border-gray-100 pb-2">2. Visa & Arrival Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Have you received your visa approval?</label>
                            <div className="flex gap-4">
                                {['Yes', 'No', 'In process'].map(opt => (
                                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="visa_status" value={opt} className="accent-[#009B8F]" /> <span>{opt}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Expected arrival month</label>
                            <input type="text" name="arrival_month" className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none" />
                        </div>
                    </div>
                </div>

                {/* 3. Guidance Areas */}
                <div>
                    <h4 className="text-lg font-bold text-[#009B8F] mb-4 border-b border-gray-100 pb-2">3. Areas of Guidance Needed</h4>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-3">Which areas would you like guidance on?</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {['UK culture & lifestyle', 'Weather & clothing', 'Cost of living', 'Local safety', 'Banking', 'SIM card', 'GP registration', 'Other'].map(opt => (
                                <label key={opt} className="flex items-center gap-2 cursor-pointer p-2 border border-gray-100 rounded hover:bg-gray-50">
                                    <input type="checkbox" name="guidance" value={opt} className="accent-[#009B8F]" /> <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 4. Supporting Documents */}
                <div>
                    <h4 className="text-lg font-bold text-[#009B8F] mb-4 border-b border-gray-100 pb-2">4. Supporting Documents (Optional)</h4>
                    <div className="space-y-4">
                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Upload any documents you have</label>
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            name="documents"
                        />
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="p-8 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-[#009B8F] hover:text-[#009B8F] hover:bg-[#009B8F]/5 transition-all cursor-pointer bg-gray-50"
                        >
                            <Upload size={32} className="mb-2" />
                            <span className="text-sm font-medium">Click to upload documents</span>
                            <span className="text-xs mt-1 text-slate-300">(Visa letter, Passport, CAS letter, etc.)</span>
                        </div>
                        {selectedFiles.length > 0 && (
                            <div className="space-y-2">
                                {selectedFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-teal-50 border border-teal-100 rounded text-sm text-[#011E26]">
                                        <span className="truncate max-w-[200px]">{file.name}</span>
                                        <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="pt-4">
                    <button type="submit" className="w-full md:w-auto px-8 py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all flex items-center justify-center gap-2">
                        Submit <ArrowRight size={16} />
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CoachingForm;
